import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { AlertTriangle, ArrowLeft, ArrowRight, Box, MapPin, Spline } from "lucide-react";
import AxisNumberInput from "../ui/AxisNumberInput";
import ConstraintEndpointMarker from "../ui/ConstraintEndpointMarker";
import DragNumberInput from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import InfoTooltip from "../ui/InfoTooltip";
import Separator from "../ui/Separator";
import MenuSelect from "../ui/MenuSelect";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import { JointIcon } from "../ui/Icons";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import EqualityVisibilityToolbar from "../../selected-constraint-visibility/EqualityVisibilityToolbar";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import {
  useBodies,
  useEqualities,
  useJoints,
  useSceneActions,
  useSites,
  useTendons,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  hasConstraintVisualizationUpdater,
  updateConstraintVisualization,
} from "../../runtime/constraintVisualizationBridge";
import { usePanelStore } from "../../stores/panelStore";
import { useViewerStore } from "../../stores/viewerStore";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { areBodiesInSameKinematicChain } from "../../utils/bodyHierarchy";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

const CONSTRAINT_TYPE_INFO =
  "connect: Constrains two bodies/sites to share an anchor point in space.\n" +
  "weld: Rigidly locks two bodies together.\n" +
  "joint: Couples two joints with a polynomial relation.\n" +
  "tendon equality: Couples two existing tendons with a polynomial relation.";

const isConnectBodyConstraint = (constraint) =>
  constraint?.type === "connect" && constraint?.objtype === "body";

const normalizeNumber = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

const isScalarJoint = (joint) => joint?.kind === "hinge" || joint?.kind === "slide";
const DEFAULT_POLYCOEF = [0, 1, 0, 0, 0];

const EqualityPropertiesPanel = ({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
}) => {
  const selectedEquality = useSelectionStore((state) => state.selectedEquality);
  const selectBody = useSelectionStore((state) => state.selectBody);
  const selectJoint = useSelectionStore((state) => state.selectJoint);
  const selectSite = useSelectionStore((state) => state.selectSite);
  const selectTendon = useSelectionStore((state) => state.selectTendon);
  const setLastSelectionSource = useSelectionStore((state) => state.setLastSelectionSource);
  const equalities = useEqualities();
  const bodies = useBodies();
  const joints = useJoints();
  const sites = useSites();
  const tendons = useTendons();
  const sceneActions = useSceneActions();
  const _setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);
  const equalityMarkerVisibility = useViewerStore((s) => s.equalityMarkerVisibility);
  const equality = selectedEquality ? equalities?.[selectedEquality] || null : null;
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();
  const authoringLocked = usePhysicsAuthoringLock();

  const [editingAnchor, setEditingAnchor] = useState({ x: 0, y: 0, z: 0 });
  const [editingAnchor2, setEditingAnchor2] = useState({ x: 0, y: 0, z: 0 });
  const [editingSolref, setEditingSolref] = useState({ timeconst: 0.02, dampratio: 2 });
  const [editingSolimp, setEditingSolimp] = useState({
    dmin: 0.9,
    dmax: 0.99,
    width: 0.001,
    mid: 0.5,
    power: 2,
  });

  const bodyOptions = useMemo(
    () =>
      Object.values(bodies || {}).map((b) => ({
        value: b.uuid,
        label: stripPrefix(b.name) || b.uuid,
      })),
    [bodies]
  );
  const jointOptions = useMemo(
    () =>
      Object.values(joints || {})
        .filter(isScalarJoint)
        .map((j) => ({ value: j.uuid, label: stripPrefix(j.name) || j.uuid })),
    [joints]
  );
  const siteOptions = useMemo(
    () =>
      Object.values(sites || {}).map((s) => ({
        value: s.uuid,
        label: stripPrefix(s.name) || s.uuid,
      })),
    [sites]
  );
  const tendonOptions = useMemo(
    () =>
      Object.values(tendons || {}).map((t) => ({
        value: t.uuid,
        label: stripPrefix(t.name) || t.uuid,
      })),
    [tendons]
  );

  const getOptionsForObjtype = (objtype) => {
    if (objtype === "body") return bodyOptions;
    if (objtype === "joint") return jointOptions;
    if (objtype === "site") return siteOptions;
    if (objtype === "tendon") return tendonOptions;
    return [];
  };

  const allowsBodyPair = (constraintType, objtype, obj1Uuid, obj2Uuid) => {
    if ((constraintType !== "connect" && constraintType !== "weld") || objtype !== "body")
      return true;
    if (!obj1Uuid || !obj2Uuid) return true;
    return !areBodiesInSameKinematicChain(bodies, obj1Uuid, obj2Uuid);
  };
  const getFirstValidUuidForObjtype = (objtype, constraintType, otherUuid, slotKey) =>
    getOptionsForObjtype(objtype).find((option) => {
      if (!otherUuid) return true;
      if (option.value === otherUuid) return false;
      return slotKey === "obj1"
        ? allowsBodyPair(constraintType, objtype, option.value, otherUuid)
        : allowsBodyPair(constraintType, objtype, otherUuid, option.value);
    })?.value || "";
  const getFirstUuidForObjtype = (objtype) => getOptionsForObjtype(objtype)[0]?.value || "";
  const sanitizeObjForType = (objUuid, objtype) => {
    const options = getOptionsForObjtype(objtype);
    if (!objUuid) return getFirstUuidForObjtype(objtype);
    return options.some((option) => option.value === objUuid)
      ? objUuid
      : getFirstUuidForObjtype(objtype);
  };
  const sanitizeObjPairForType = (
    obj1Uuid,
    obj2Uuid,
    objtype,
    changedKey = null,
    constraintType = equality?.type
  ) => {
    let obj1 = sanitizeObjForType(obj1Uuid, objtype);
    let obj2 = sanitizeObjForType(obj2Uuid, objtype);
    if (obj1 && obj2 && (obj1 === obj2 || !allowsBodyPair(constraintType, objtype, obj1, obj2))) {
      if (changedKey === "obj2") {
        obj1 = getFirstValidUuidForObjtype(objtype, constraintType, obj2, "obj1");
      } else {
        obj2 = getFirstValidUuidForObjtype(objtype, constraintType, obj1, "obj2");
      }
    }
    return {
      obj1: obj1 || undefined,
      obj2: obj2 || undefined,
    };
  };

  const detectObjTypeByUuid = (objUuid) => {
    if (!objUuid) return null;
    if (bodies[objUuid]) return "body";
    if (joints[objUuid]) return "joint";
    if (sites[objUuid]) return "site";
    if (tendons[objUuid]) return "tendon";
    return null;
  };

  const resolveObjtypeForConstraintType = (constraint, nextType) => {
    if (nextType === "joint") return "joint";
    if (nextType === "tendon") return "tendon";
    if (nextType === "connect" || nextType === "weld") {
      const obj1Type = detectObjTypeByUuid(constraint?.obj1);
      const obj2Type = detectObjTypeByUuid(constraint?.obj2);
      if (obj1Type && obj1Type === obj2Type && (obj1Type === "body" || obj1Type === "site"))
        return obj1Type;
      return constraint?.objtype === "site" ? "site" : "body";
    }
    return constraint?.objtype || "body";
  };

  useEffect(() => {
    if (!equality) {
      updateConstraintVisualization([]);
      return;
    }
    const data = Array.isArray(equality.data) ? equality.data : [];
    setEditingAnchor({
      x: normalizeNumber(data[0]),
      y: normalizeNumber(data[1]),
      z: normalizeNumber(data[2]),
    });
    setEditingSolref({
      timeconst: normalizeNumber(equality.solref?.[0], 0.02),
      dampratio: normalizeNumber(equality.solref?.[1], 2),
    });
    setEditingSolimp({
      dmin: normalizeNumber(equality.solimp?.[0], 0.9),
      dmax: normalizeNumber(equality.solimp?.[1], 0.99),
      width: normalizeNumber(equality.solimp?.[2], 0.001),
      mid: normalizeNumber(equality.solimp?.[3], 0.5),
      power: normalizeNumber(equality.solimp?.[4], 2),
    });
  }, [equality]);

  useEffect(() => {
    if (!equality || !isConnectBodyConstraint(equality)) {
      setEditingAnchor2({ x: 0, y: 0, z: 0 });
      return;
    }
    const body1Obj = equality.obj1 ? window.sceneObjectsRef?.current?.[equality.obj1] : null;
    const body2Obj = equality.obj2 ? window.sceneObjectsRef?.current?.[equality.obj2] : null;
    if (!body1Obj || !body2Obj) {
      setEditingAnchor2({ x: 0, y: 0, z: 0 });
      return;
    }
    body1Obj.updateMatrixWorld(true);
    body2Obj.updateMatrixWorld(true);
    const localPos1 = new THREE.Vector3(editingAnchor.x, editingAnchor.y, editingAnchor.z);
    const worldPos = localPos1.clone().applyMatrix4(body1Obj.matrixWorld);
    const invMatrix2 = new THREE.Matrix4().copy(body2Obj.matrixWorld).invert();
    const localPos2 = worldPos.clone().applyMatrix4(invMatrix2);
    setEditingAnchor2({ x: localPos2.x, y: localPos2.y, z: localPos2.z });
  }, [equality, editingAnchor]);

  useEffect(() => {
    const perItemVisible =
      equality?.uuid != null && (equalityMarkerVisibility[equality.uuid] ?? true);
    const constraintsToShow =
      equality && perItemVisible && isConnectBodyConstraint(equality) ? [equality] : [];
    if (constraintsToShow.length === 0) {
      updateConstraintVisualization([]);
      return;
    }

    let retryCount = 0;
    let retryTimeout = null;
    const maxRetries = 20;
    const tryUpdate = () => {
      if (hasConstraintVisualizationUpdater()) {
        updateConstraintVisualization(constraintsToShow);
        return;
      }
      retryCount += 1;
      if (retryCount < maxRetries) retryTimeout = setTimeout(tryUpdate, 100);
    };

    retryTimeout = setTimeout(tryUpdate, 100);
    return () => {
      if (retryTimeout) window.clearTimeout(retryTimeout);
    };
  }, [equality, equalityMarkerVisibility]);

  useEffect(() => () => updateConstraintVisualization([]), []);

  if (!equality) return null;

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";
  const backLabel = canGoBack ? `Back (ALT + <-): ${quickNavBackTarget.label}` : "Back (ALT + <-)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + ->): ${quickNavForwardTarget.label}`
    : "Forward (ALT + ->)";
  const renderTargetIcon = (treeSlot) => {
    const showSlot = treeSlot === 1 || treeSlot === 2;

    const makeSlotBadge = (positionClass) =>
      showSlot ? (
        <span
          className={`pointer-events-none absolute text-[8px] font-bold leading-none text-gray-100 ${positionClass}`}
          style={{ textShadow: "0 0 2px rgb(0 0 0), 0 0 1px rgb(0 0 0)" }}
        >
          {treeSlot}
        </span>
      ) : null;

    const wrapWithSlot = (iconEl, badgePositionClass) => {
      if (!showSlot) return iconEl;
      return (
        <span className="relative inline-flex h-4 w-4 shrink-0" aria-hidden="true">
          {iconEl}
          {makeSlotBadge(badgePositionClass)}
        </span>
      );
    };

    /** Pinch joint / tendon glyphs: digit sits just past the lower-right of the symbol. */
    const badgeNearJointLike = "bottom-0 right-0 translate-x-px translate-y-px";
    /** Lucide box / pin: nudge digit outward so it does not cover the glyph. */
    const badgeBesideLucideBodyOrSite = "right-[-5px] bottom-[-1px]";

    if (equality.objtype === "joint") {
      return wrapWithSlot(<JointIcon className="h-4 w-4" aria-hidden="true" />, badgeNearJointLike);
    }
    if (equality.objtype === "site") {
      return wrapWithSlot(
        <MapPin className="h-4 w-4" aria-hidden="true" />,
        badgeBesideLucideBodyOrSite
      );
    }
    if (equality.objtype === "tendon") {
      return wrapWithSlot(<Spline className="h-4 w-4" aria-hidden="true" />, badgeNearJointLike);
    }
    return wrapWithSlot(
      <Box className="h-4 w-4" aria-hidden="true" />,
      badgeBesideLucideBodyOrSite
    );
  };
  const targetLabelPrefix =
    equality.objtype === "joint"
      ? "Joint"
      : equality.objtype === "site"
        ? "Site"
        : equality.objtype === "tendon"
          ? "Tendon"
          : "Body";
  const getTargetName = (targetUuid) => {
    if (!targetUuid) return "";
    if (equality.objtype === "joint") return stripPrefix(joints[targetUuid]?.name) || targetUuid;
    if (equality.objtype === "site") return stripPrefix(sites[targetUuid]?.name) || targetUuid;
    if (equality.objtype === "tendon") return stripPrefix(tendons[targetUuid]?.name) || targetUuid;
    return stripPrefix(bodies[targetUuid]?.name) || targetUuid;
  };
  const inspectTarget = (targetUuid) => {
    if (!targetUuid) return;
    setLastSelectionSource("sceneTree");
    if (equality.objtype === "joint") {
      selectJoint(targetUuid);
    } else if (equality.objtype === "site") {
      selectSite(targetUuid);
    } else if (equality.objtype === "tendon") {
      selectTendon(targetUuid);
    } else {
      selectBody(targetUuid);
    }
    requestInspectorChromeFlash();
  };
  const _quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    {
      key: "obj1",
      label: equality.obj1
        ? `Inspect ${targetLabelPrefix} 1: ${getTargetName(equality.obj1)}`
        : `No ${targetLabelPrefix.toLowerCase()} 1`,
      icon: renderTargetIcon(1),
      disabled: !equality.obj1,
      onDefaultClick: () => inspectTarget(equality.obj1),
    },
    {
      key: "obj2",
      label: equality.obj2
        ? `Inspect ${targetLabelPrefix} 2: ${getTargetName(equality.obj2)}`
        : `No ${targetLabelPrefix.toLowerCase()} 2`,
      icon: renderTargetIcon(2),
      disabled: !equality.obj2,
      onDefaultClick: () => inspectTarget(equality.obj2),
    },
  ];

  const updateEquality = (patch) => {
    if (!equality.uuid) return;
    sceneActions.updateEquality(equality.uuid, patch);
  };

  const refreshSelectedConstraintVisualization = (patch) => {
    const perItemVisible =
      equality?.uuid != null && (equalityMarkerVisibility[equality.uuid] ?? true);
    if (!perItemVisible || !isConnectBodyConstraint(equality)) return;
    updateConstraintVisualization([{ ...equality, ...patch }]);
  };

  const getAnchorBodyObjects = () => {
    if (!isConnectBodyConstraint(equality)) return null;
    const body1Obj = equality.obj1 ? window.sceneObjectsRef?.current?.[equality.obj1] : null;
    const body2Obj = equality.obj2 ? window.sceneObjectsRef?.current?.[equality.obj2] : null;
    if (!body1Obj || !body2Obj) return null;
    body1Obj.updateMatrixWorld(true);
    body2Obj.updateMatrixWorld(true);
    return { body1Obj, body2Obj };
  };

  const handleUpdateType = (nextType) => {
    const nextObjtype = resolveObjtypeForConstraintType(equality, nextType);
    const nextObjects = sanitizeObjPairForType(
      equality.obj1,
      equality.obj2,
      nextObjtype,
      null,
      nextType
    );
    updateEquality({
      type: nextType,
      objtype: nextObjtype,
      ...nextObjects,
      data:
        nextType === "connect" && nextObjtype === "body"
          ? Array.isArray(equality.data) && equality.data.length >= 3
            ? equality.data.slice(0, 3)
            : [0, 0, 0]
          : nextType === "joint" || nextType === "tendon"
            ? DEFAULT_POLYCOEF
            : equality.data,
    });
  };

  const handleUpdateAnchor = (axis, value) => {
    const next = { ...editingAnchor, [axis]: normalizeNumber(value) };
    setEditingAnchor(next);
    const data = [next.x, next.y, next.z];
    updateEquality({ data });
    refreshSelectedConstraintVisualization({ data });
  };

  const handleUpdateAnchor2 = (axis, value) => {
    const nextAnchor2 = { ...editingAnchor2, [axis]: normalizeNumber(value) };
    setEditingAnchor2(nextAnchor2);

    const anchorBodyObjects = getAnchorBodyObjects();
    if (!anchorBodyObjects) return;

    const { body1Obj, body2Obj } = anchorBodyObjects;
    const body2LocalPos = new THREE.Vector3(nextAnchor2.x, nextAnchor2.y, nextAnchor2.z);
    const worldPos = body2LocalPos.clone().applyMatrix4(body2Obj.matrixWorld);
    const body1LocalPos = worldPos
      .clone()
      .applyMatrix4(new THREE.Matrix4().copy(body1Obj.matrixWorld).invert());
    const nextAnchor = {
      x: body1LocalPos.x,
      y: body1LocalPos.y,
      z: body1LocalPos.z,
    };
    const data = [nextAnchor.x, nextAnchor.y, nextAnchor.z];
    setEditingAnchor(nextAnchor);
    updateEquality({ data });
    refreshSelectedConstraintVisualization({ data });
  };

  const handleUpdateSolref = (field, value) => {
    const next = { ...editingSolref, [field]: normalizeNumber(value) };
    setEditingSolref(next);
    updateEquality({ solref: [next.timeconst, next.dampratio] });
  };

  const handleUpdateSolimp = (field, value) => {
    const next = { ...editingSolimp, [field]: normalizeNumber(value) };
    setEditingSolimp(next);
    updateEquality({ solimp: [next.dmin, next.dmax, next.width, next.mid, next.power] });
  };

  const renderAxisField = ({ axis, value, onChange, readOnly = false }) => (
    <AxisNumberInput
      axis={axis.toLowerCase()}
      value={value}
      dragValue={Number(value) || 0}
      onChange={(event) => onChange?.(event.target.value)}
      onDragValueChange={onChange}
      deferDragUpdates={false}
      inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-xs"
      inputProps={{ step: "0.01", readOnly }}
    />
  );

  const renderDragNumberField = ({ label, value, onChange, step = "0.01" }) => (
    <DragNumberInput
      label={label}
      value={value}
      dragValue={Number(value) || 0}
      onChange={(event) => onChange(event.target.value)}
      onDragValueChange={onChange}
      inputClassName="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-gray-100"
      inputProps={{ step }}
    />
  );

  const targetLabels =
    equality.objtype === "joint"
      ? ["Joint 1", "Joint 2"]
      : equality.objtype === "tendon"
        ? ["Existing Tendon 1", "Existing Tendon 2"]
        : equality.objtype === "site"
          ? ["Site 1", "Site 2"]
          : ["Body 1", "Body 2"];
  const targetOptionsForCurrentType = getOptionsForObjtype(equality.objtype);
  const missingTargetPair = !equality.obj1 || !equality.obj2;
  const hasTooFewDistinctTargets = targetOptionsForCurrentType.length < 2;
  const targetTypeLabel = targetLabelPrefix.toLowerCase();
  const targetPairWarning =
    missingTargetPair && hasTooFewDistinctTargets
      ? `This ${equality.type} constraint needs two distinct ${targetTypeLabel}s. Add another ${targetTypeLabel} or choose a different target type; otherwise it will be skipped when physics starts and when MJCF is exported.`
      : missingTargetPair
        ? `This ${equality.type} constraint is missing a ${targetTypeLabel} target. Select both targets or it will be skipped when physics starts and when MJCF is exported.`
        : "";

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <EqualityVisibilityToolbar equalityUuid={equality.uuid} />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <EqualityVisibilityToolbar equalityUuid={equality.uuid} />

      <Separator />

      <div>
        <FieldLabel as="label" className="mb-1 flex items-center gap-1">
          Constraint Type
          <InfoTooltip text={CONSTRAINT_TYPE_INFO} />
        </FieldLabel>
        <MenuSelect
          size="compact"
          value={equality.type || "connect"}
          onChange={handleUpdateType}
          options={[
            {
              label: "Body / Site Equalities",
              options: [
                { value: "connect", label: "connect" },
                { value: "weld", label: "weld" },
              ],
            },
            {
              label: "Coupling Equalities",
              options: [
                { value: "joint", label: "joint" },
                { value: "tendon", label: "tendon equality" },
              ],
            },
          ]}
          triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
          aria-label="Constraint type"
        />
      </div>

      {equality.type === "connect" ? (
        <div>
          <FieldLabel as="label" className="block mb-1">
            Target Type
          </FieldLabel>
          <MenuSelect
            size="compact"
            value={equality.objtype || "body"}
            onChange={(nextObjtype) => {
              const nextObjects = sanitizeObjPairForType(
                equality.obj1,
                equality.obj2,
                nextObjtype,
                null,
                equality.type
              );
              updateEquality({
                objtype: nextObjtype,
                ...nextObjects,
              });
            }}
            options={[
              { value: "body", label: "body" },
              { value: "site", label: "site" },
            ]}
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Constraint target type"
          />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map((index) => {
          const key = index === 0 ? "obj1" : "obj2";
          return (
            <div key={key}>
              <FieldLabel as="label" className="block mb-1">
                {targetLabels[index]}
              </FieldLabel>
              <MenuSelect
                size="compact"
                value={equality[key] || ""}
                onChange={(selectedUuid) => {
                  const v = selectedUuid || undefined;
                  const nextObjects = sanitizeObjPairForType(
                    key === "obj1" ? v : equality.obj1,
                    key === "obj2" ? v : equality.obj2,
                    equality.objtype,
                    key,
                    equality.type
                  );
                  updateEquality(nextObjects);
                }}
                disabled={getOptionsForObjtype(equality.objtype).length === 0}
                placeholder={
                  getOptionsForObjtype(equality.objtype).length === 0
                    ? "No targets"
                    : "Select target"
                }
                options={getOptionsForObjtype(equality.objtype).map((option) => ({
                  value: option.value,
                  label: option.label,
                  disabled:
                    option.value === equality[index === 0 ? "obj2" : "obj1"] ||
                    !allowsBodyPair(
                      equality.type,
                      equality.objtype,
                      index === 0 ? option.value : equality.obj1,
                      index === 1 ? option.value : equality.obj2
                    ),
                }))}
                contentClassName="max-h-[min(240px,50vh)]"
                triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                aria-label={targetLabels[index]}
              />
            </div>
          );
        })}
      </div>

      {targetPairWarning ? (
        <div className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <div>{targetPairWarning}</div>
        </div>
      ) : null}

      {isConnectBodyConstraint(equality) ? (
        <>
          <Separator />

          <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
            <Collapsible defaultOpen>
              <CollapsibleTrigger quiet>Anchor Points</CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pt-2 space-y-3">
                  <div>
                    <FieldLabel className="mb-1 flex items-center gap-1.5">
                      <ConstraintEndpointMarker variant="solid" />
                      <span>
                        {stripPrefix(bodies[equality.obj1]?.name) || "Body 1"} local coordinates
                      </span>
                    </FieldLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {["x", "y", "z"].map((axis) => (
                        <div key={axis}>
                          {renderAxisField({
                            axis,
                            value: editingAnchor[axis],
                            onChange: (value) => handleUpdateAnchor(axis, value),
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <FieldLabel className="mb-1 flex items-center gap-1.5">
                      <ConstraintEndpointMarker variant="wire" />
                      <span>
                        {stripPrefix(bodies[equality.obj2]?.name) || "Body 2"} local coordinates
                      </span>
                    </FieldLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {["x", "y", "z"].map((axis) => (
                        <div key={axis}>
                          {renderAxisField({
                            axis,
                            value: editingAnchor2[axis],
                            onChange: (value) => handleUpdateAnchor2(axis, value),
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </InspectorPhysicsLockedCollapsibleWrap>
        </>
      ) : null}

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible>
          <CollapsibleTrigger quiet>Solver Settings</CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-4">
              <div>
                <div className="text-xs font-medium text-gray-200 mb-2">
                  Solver Reference (solref)
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {renderDragNumberField({
                    label: "Time Constant",
                    value: editingSolref.timeconst,
                    onChange: (value) => handleUpdateSolref("timeconst", value),
                    step: "0.001",
                  })}
                  {renderDragNumberField({
                    label: "Damping Ratio",
                    value: editingSolref.dampratio,
                    onChange: (value) => handleUpdateSolref("dampratio", value),
                    step: "0.1",
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-200 mb-2">
                  Solver Impedance (solimp)
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {renderDragNumberField({
                    label: "dmin",
                    value: editingSolimp.dmin,
                    onChange: (value) => handleUpdateSolimp("dmin", value),
                  })}
                  {renderDragNumberField({
                    label: "dmax",
                    value: editingSolimp.dmax,
                    onChange: (value) => handleUpdateSolimp("dmax", value),
                  })}
                  {renderDragNumberField({
                    label: "width",
                    value: editingSolimp.width,
                    onChange: (value) => handleUpdateSolimp("width", value),
                    step: "0.0001",
                  })}
                  {renderDragNumberField({
                    label: "mid",
                    value: editingSolimp.mid,
                    onChange: (value) => handleUpdateSolimp("mid", value),
                    step: "0.1",
                  })}
                  {renderDragNumberField({
                    label: "power",
                    value: editingSolimp.power,
                    onChange: (value) => handleUpdateSolimp("power", value),
                    step: "0.1",
                  })}
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>
    </div>
  );
};

export default React.memo(EqualityPropertiesPanel);
